import { useParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { getUpdatedPayload } from 'utils/helpers';
import { useClientPricingRulesUpdateMutation } from 'api';
import { Form, FormInstance, FormProps } from 'components';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { ClientPricingRuleType, IPricingRulesEditModal } from 'feature/clientTabs/payments/pricingRules/edit/index';

export const usePricingRulesEditModal = (props: IPricingRulesEditModal) => {
  const { modalState, dataToEdit, onModalStateChange } = props;
  const { id } = useParams() as { id: string };
  const [form] = Form.useForm();
  const { handleError, handleSuccess } = useNotificationMessages();

  const [updatePricingRules, { isLoading }] = useClientPricingRulesUpdateMutation();
  const clearFieldValidation = useCallback((form: FormInstance, field: string) => {
    const hasError = form.getFieldError(field)?.length;
    if (hasError) {
      form.setFields([
        {
          name: field,
          errors: [], // Clears the error for filed
        },
      ]);
    }
  }, []);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish: FormProps<ClientPricingRuleType>['onFinish'] = useCallback(
    async (values: ClientPricingRuleType) => {
      const payload = getUpdatedPayload(values, dataToEdit);
      payload.id = id;
      try {
        const { data, error }: Record<string, any> = await updatePricingRules(payload);
        if (data?.success) {
          handleSuccess('Pricing rules was successfully updated.');
          onCancel();
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [updatePricingRules, id, onCancel, dataToEdit, handleError, handleSuccess]
  );

  useEffect(() => {
    if (modalState && dataToEdit) {
      form.setFieldsValue(dataToEdit);
    }
  }, [modalState, dataToEdit, form]);

  return {
    onFinish,
    onCancel,
    form,
    isLoading,
    clearFieldValidation,
  };
};
